import { Seo } from '../components/Seo';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ExperienceCrossSell } from '../components/ExperienceCrossSell';
import { HotelCard } from '../components/OfferCards';
import { HotelMap } from '../components/HotelMap';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { FilteredResults } from '../components/filters/FilteredResults';
import { SCHEMAS } from '../lib/filterSchemas';
import { DestinationTiles } from '../components/DiscoverySections';
import { FaqSection } from '../components/FaqSection';
import { SearchProgress } from '../components/SearchProgress';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { openDeal } from '../lib/deals';
import { usePersistentState, clearPersisted } from '../lib/usePersistentState';
import { fmtDateMed } from '../lib/dateFormat';
import { useNavigate } from 'react-router-dom';
import { Hotel, Search, MapPin, ArrowLeft, List, Map as MapIcon, Loader2 } from 'lucide-react';

function todayPlus(d) { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); }

export default function Hotels() {
  const [params, setParams] = useSearchParams();
  const { currency, promptEsim } = useApp();
  const navigate = useNavigate();
  const bookHotel = (offer, surface) => {
    openDeal({ type: 'hotel', offer, surface, source: '/hotels' });
    promptEsim({ country_code: offer.country_code, country: offer.country, city: offer.city });
  };
  const openHotelPage = (offer) => navigate(`/hotels/${offer.hotel_id}?ci=${checkin}&co=${checkout}&g=${guests}`);
  const [dest, setDest] = usePersistentState('hotels:dest', null);
  const [q, setQ] = usePersistentState('hotels:q', '');
  const [sugg, setSugg] = useState([]);
  const [hotelSugg, setHotelSugg] = useState([]);
  const [openSugg, setOpenSugg] = useState(false);
  const [checkin, setCheckin] = usePersistentState('hotels:checkin', todayPlus(14));
  const [checkout, setCheckout] = usePersistentState('hotels:checkout', todayPlus(17));
  const [guests, setGuests] = usePersistentState('hotels:guests', 2);
  const [rooms, setRooms] = usePersistentState('hotels:rooms', 1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [data, setData] = usePersistentState('hotels:data', null);
  const [offers, setOffers] = usePersistentState('hotels:offers', []);
  const [lastDest, setLastDest] = usePersistentState('hotels:lastdest', '');
  const [view, setView] = usePersistentState('hotels:view', 'list');
  const [discovery, setDiscovery] = useState(null);
  const debRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [progress, setProgress] = useState(0);
  const optsRef = useRef({});
  const pageRef = useRef(1);
  const STREAM_TARGET = 300;

  const nights = Math.max(1, Math.round((new Date(checkout) - new Date(checkin)) / 86400000));

  const fetchPage = useCallback(async (pageNum, opts = {}) => {
    const cityId = opts.agoda_city_id ?? dest?.agoda_city_id;
    const code = opts.code ?? dest?.code;
    const hq = opts.hotel_query;
    const ci = opts.checkin ?? checkin;
    const co = opts.checkout ?? checkout;
    const g = opts.guests ?? guests;
    const rm = opts.rooms ?? rooms;
    const nts = Math.max(1, Math.round((new Date(co) - new Date(ci)) / 86400000));
    const { data: res } = await api.hotels({
      ...(hq ? { hotel_query: hq } : {}),
      ...(!hq && cityId ? { agoda_city_id: cityId } : {}),
      ...(!hq && code ? { destination_code: code } : {}),
      checkin: ci, checkout: co, guests: g, rooms: rm, nights: nts, currency, page: pageNum, page_size: 60,
    });
    return res;
  }, [dest, checkin, checkout, guests, rooms, currency]);

  // Runs a search and STREAMS more pages in (up to ~STREAM_TARGET stays) so the user quickly sees
  // hundreds of live-priced results, with the progress bar advancing as each page lands.
  const runSearch = useCallback(async (opts = {}) => {
    const hq = opts.hotel_query;
    if (!hq && !(opts.agoda_city_id ?? dest?.agoda_city_id) && !(opts.code ?? dest?.code)) return;
    optsRef.current = opts;
    setLoading(true); setStreaming(false); setProgress(8); setOffers([]); setData(null);
    try {
      const first = await fetchPage(1, opts);
      pageRef.current = first.page || 1;
      setData(first); setOffers(first.offers || []);
      setLoading(false);
      let acc = (first.offers || []).length;
      let hasMore = first.has_more;
      setProgress(Math.max(12, Math.min(96, Math.round((acc / STREAM_TARGET) * 100))));
      if (hasMore && acc < STREAM_TARGET) {
        setStreaming(true);
        let page = pageRef.current;
        while (hasMore && acc < STREAM_TARGET && page < 8) {
          page += 1;
          // eslint-disable-next-line no-await-in-loop
          const res = await fetchPage(page, opts);
          pageRef.current = res.page || page;
          setData(res);
          setOffers((prev) => [...prev, ...(res.offers || [])]);
          acc += (res.offers || []).length;
          hasMore = res.has_more;
          setProgress(Math.min(96, Math.round((acc / STREAM_TARGET) * 100)));
        }
        setStreaming(false);
      }
      setProgress(100);
      setTimeout(() => setProgress(0), 800);
    } catch (e) {
      console.error('Hotel search failed', e);
      setLoading(false); setStreaming(false); setProgress(0);
    }
  }, [dest, fetchPage]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const res = await fetchPage((pageRef.current || 1) + 1, optsRef.current);
      pageRef.current = res.page || (pageRef.current + 1);
      setData(res);
      setOffers((prev) => [...prev, ...(res.offers || [])]);
    } catch (e) { console.error('Load more failed', e); } finally { setLoadingMore(false); }
  }, [fetchPage]);

  useEffect(() => {
    api.discoveryHotels().then(({ data }) => setDiscovery(data)).catch(() => {});
    const code = params.get('destination_code');
    // Re-run when arriving from a NEW cross-sell (different destination) OR on first load.
    // When returning from a hotel detail page (same destination, results restored) we skip so the
    // exact loaded results are preserved. This fixes stale results (e.g. showing Bengaluru after a
    // new CCU→DPS flight search should open Bali, not the previous city).
    if (code && (code !== lastDest || !data)) {
      setLastDest(code);
      setData(null); setOffers([]);
      // Trip dates + guests carried from the flight cross-sell (fall back to current state).
      const ci = params.get('ci'); const co = params.get('co'); const g = params.get('g');
      if (ci) setCheckin(ci);
      if (co) setCheckout(co);
      if (g) setGuests(Number(g));
      const dateOpts = { ...(ci ? { checkin: ci } : {}), ...(co ? { checkout: co } : {}), ...(g ? { guests: Number(g) } : {}) };
      // The incoming value may be a city NAME (e.g. from the flight cross-sell "Hotels in Bengaluru")
      // rather than a mapped code. Resolve it to a real Agoda city first — same path typing uses —
      // so the auto-search returns results. Fall back to treating it as a code.
      setQ(code);
      setLoading(true);
      (async () => {
        const wantCountry = (params.get('country') || '').trim();
        const wantCC = (params.get('country_code') || '').trim();
        const destIata = (params.get('dest_iata') || '').trim();
        const resolveWith = async (term) => {
          const { data: loc } = await api.hotelsLocations(term, { country: wantCountry, country_code: wantCC });
          return loc.results || [];
        };
        try {
          let results = await resolveWith(code);
          // If the incoming value was a city NAME that produced no country-matched hit, retry
          // with the IATA code (backend resolves codes exactly, e.g. DEL -> New Delhi).
          const hasGood = results.length && (!wantCountry ||
            results.some((r) => (r.country || '').trim().toLowerCase() === wantCountry.toLowerCase()) ||
            (wantCC && results.some((r) => (r.country_code || '').toUpperCase() === wantCC.toUpperCase())));
          if (!hasGood && destIata && destIata.toUpperCase() !== code.toUpperCase()) {
            const alt = await resolveWith(destIata);
            if (alt.length) results = alt;
          }
          const wantCountryLc = wantCountry.toLowerCase();
          let top = results[0];
          const match = results.find((r) =>
            (wantCC && (r.country_code || '').toUpperCase() === wantCC.toUpperCase()) ||
            (wantCountryLc && (r.country || '').trim().toLowerCase() === wantCountryLc));
          if (match) top = match;
          if (top && top.agoda_city_id) {
            setDest({ agoda_city_id: top.agoda_city_id, name: top.name, state: top.state, country: top.country });
            setQ(top.name);
            runSearch({ agoda_city_id: top.agoda_city_id, ...dateOpts });
            return;
          }
        } catch { /* fall through to code-based search */ }
        setDest({ code, name: code });
        runSearch({ code, ...dateOpts });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get('destination_code')]);

  // typeahead
  useEffect(() => {
    if (!openSugg) return;
    if (debRef.current) clearTimeout(debRef.current);
    if ((q || '').trim().length < 2) { setSugg([]); setHotelSugg([]); return; }
    debRef.current = setTimeout(async () => {
      const term = q.trim();
      try { const { data } = await api.hotelsLocations(term); setSugg(data.results || []); }
      catch { setSugg([]); }
      try { const { data } = await api.hotelsNameSearch(term); setHotelSugg(data.results || []); }
      catch { setHotelSugg([]); }
    }, 250);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [q, openSugg]);

  const pickCity = (s) => {
    const d = { agoda_city_id: s.agoda_city_id, name: s.name, state: s.state, country: s.country };
    setDest(d); setQ(s.name); setOpenSugg(false); setSugg([]);
    setParams((p) => { p.delete('destination_code'); return p; }, { replace: true });
    setOffers([]); runSearch({ agoda_city_id: s.agoda_city_id });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Search by hotel NAME / BRAND (e.g. "Taj" -> every Taj property) using the same box.
  const searchBrand = (term) => {
    const t = (term || q || '').trim();
    if (t.length < 2) return;
    setDest(null); setQ(t); setOpenSugg(false); setSugg([]); setHotelSugg([]);
    setParams((p) => { p.delete('destination_code'); return p; }, { replace: true });
    setOffers([]); runSearch({ hotel_query: t });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectDestination = (t) => {
    const code = t.search_code || t.destination_code || t.code;
    setDest({ code, name: t.name }); setQ(t.name);
    setParams((p) => { p.set('destination_code', code); return p; }, { replace: true });
    setOffers([]); runSearch({ code });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearSearch = () => { setData(null); setOffers([]); setDest(null); setQ(''); clearPersisted('hotels:'); setParams({}, { replace: true }); };

  return (
    <Layout>
      <Seo title="Hotels — Compare Stays by Price & Rating" path="/hotels"
        description="Compare live hotel prices across thousands of stays worldwide on FlightDeals.in. Filter by price, rating, amenities and book with our trusted partner — no booking fees." />
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2"><Hotel className="h-7 w-7 text-accent" /> Compare hotels</h1>
          <p className="text-primary-foreground/75 mt-1">Search any city worldwide — live prices, real reviews & photos, then book with our trusted partner.</p>
          <div className="mt-5 rounded-2xl bg-card text-foreground p-4 flex flex-col md:flex-row md:flex-wrap md:items-end gap-3">
            <div className="relative w-full md:flex-[2_1_220px] md:min-w-[200px]">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Destination / city</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={q} placeholder="City, area, region or hotel name (e.g. Bali, Taj)" data-testid="hotel-destination-input"
                  onChange={(e) => { setQ(e.target.value); setOpenSugg(true); }} onFocus={() => setOpenSugg(true)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); dest ? runSearch(dest.agoda_city_id ? { agoda_city_id: dest.agoda_city_id } : { code: dest.code }) : searchBrand(q); } }}
                  onBlur={() => setTimeout(() => setOpenSugg(false), 180)} className="h-12 w-full pl-9" />
              </div>
              {openSugg && (q || '').trim().length >= 2 && (sugg.length > 0 || hotelSugg.length > 0 || true) && (
                <div className="absolute z-30 mt-1 w-full max-h-80 overflow-auto rounded-xl border border-border bg-card shadow-lg" data-testid="hotel-suggestions">
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => searchBrand(q)}
                    className="w-full text-left px-3 py-2.5 hover:bg-secondary flex items-center gap-2 border-b border-border" data-testid="hotel-brand-search-all">
                    <Search className="h-4 w-4 text-accent shrink-0" />
                    <span className="text-sm">Search all <span className="font-semibold">“{q.trim()}”</span> properties</span>
                  </button>
                  {hotelSugg.length > 0 && (
                    <>
                      <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Hotels</p>
                      {hotelSugg.map((h) => (
                        <button key={`h-${h.hotel_id}`} type="button" onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { setOpenSugg(false); navigate(`/hotels/${h.hotel_id}?ci=${checkin}&co=${checkout}&g=${guests}`); }}
                          className="w-full text-left px-3 py-2 hover:bg-secondary flex items-center gap-2" data-testid={`hotel-name-sugg-${h.hotel_id}`}>
                          <Hotel className="h-4 w-4 text-accent shrink-0" />
                          <span className="min-w-0"><span className="font-medium truncate block">{h.name}</span>
                            <span className="block text-[11px] text-muted-foreground truncate">{h.star_rating ? '★'.repeat(h.star_rating) + ' · ' : ''}{h.sub}</span></span>
                        </button>
                      ))}
                      {sugg.length > 0 && <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border-t border-border">Cities & areas</p>}
                    </>
                  )}
                  {sugg.map((s) => (
                    <button key={s.agoda_city_id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickCity(s)}
                      className="w-full text-left px-3 py-2 hover:bg-secondary flex items-center gap-2" data-testid={`hotel-sugg-${s.agoda_city_id}`}>
                      <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="min-w-0"><span className="font-medium">{s.name}</span>
                        <span className="block text-[11px] text-muted-foreground truncate">{s.sub}</span></span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="w-full md:flex-[1_1_140px] md:min-w-[130px]"><Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Check-in</Label><Input type="date" value={checkin} onChange={(e) => setCheckin(e.target.value)} className="h-12 w-full min-w-0 text-sm px-3" data-testid="hotel-checkin" /></div>
            <div className="w-full md:flex-[1_1_140px] md:min-w-[130px]"><Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Check-out</Label><Input type="date" value={checkout} onChange={(e) => setCheckout(e.target.value)} className="h-12 w-full min-w-0 text-sm px-3" data-testid="hotel-checkout" /></div>
            <div className="flex gap-3">
              <div className="flex-1 md:w-20"><Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Guests</Label><Input type="number" min={1} value={guests} onChange={(e) => setGuests(e.target.value)} className="h-12 w-full min-w-0" data-testid="hotel-guests" /></div>
              <div className="flex-1 md:w-20"><Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Rooms</Label><Input type="number" min={1} value={rooms} onChange={(e) => setRooms(e.target.value)} className="h-12 w-full min-w-0" data-testid="hotel-rooms" /></div>
            </div>
            <div className="w-full md:w-auto md:flex-[0_0_auto]"><Button onClick={() => dest ? runSearch(dest.agoda_city_id ? { agoda_city_id: dest.agoda_city_id } : { code: dest.code }) : searchBrand(q)} disabled={!dest && (q || '').trim().length < 2} className="w-full md:w-40 h-12 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="hotel-search-button"><Search className="h-4 w-4 mr-1.5" /> Search</Button></div>
          </div>
          <p className="mt-2 text-xs text-primary-foreground/70" data-testid="hotel-date-summary">
            {fmtDateMed(checkin)} → {fmtDateMed(checkout)} · {nights} night{nights > 1 ? 's' : ''} · {guests} guest{Number(guests) > 1 ? 's' : ''} · {rooms} room{Number(rooms) > 1 ? 's' : ''}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4"><AffiliateDisclosure variant="banner" /></div>

        <SearchProgress
          active={loading || streaming || progress > 0}
          value={loading && offers.length === 0 ? 12 : Math.max(progress, Math.min(96, Math.round((offers.length / STREAM_TARGET) * 100)))}
          label={streaming ? `Loading more stays… ${offers.length} found` : (loading ? 'Fetching live hotel prices…' : '')}
          testid="hotel-search-progress"
        />

        {loading && offers.length === 0 && <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-44 rounded-2xl" />)}</div>}

        {!loading && data && (
          <>
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <p className="text-sm text-muted-foreground" data-testid="hotel-results-summary">
                {data.total_hotels ? `${data.total_hotels.toLocaleString('en-IN')} stays in ${data.destination.city}` : `Hotels in ${data.destination.city}`}
              </p>
              <div className="flex items-center gap-2">
                {!data.city_unmapped && offers.length > 0 && (
                  <div className="inline-flex rounded-lg border border-border overflow-hidden">
                    <button onClick={() => setView('list')} className={`px-3 h-8 text-xs font-semibold inline-flex items-center gap-1 ${view === 'list' ? 'bg-accent text-accent-foreground' : 'bg-card text-muted-foreground'}`} data-testid="hotel-view-list"><List className="h-3.5 w-3.5" /> List</button>
                    <button onClick={() => setView('map')} className={`px-3 h-8 text-xs font-semibold inline-flex items-center gap-1 ${view === 'map' ? 'bg-accent text-accent-foreground' : 'bg-card text-muted-foreground'}`} data-testid="hotel-view-map"><MapIcon className="h-3.5 w-3.5" /> Map</button>
                  </div>
                )}
                <Button variant="ghost" size="sm" onClick={clearSearch} className="text-xs" data-testid="hotel-back-to-discovery"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Discover more</Button>
              </div>
            </div>

            {data.city_unmapped ? (
              <div className="rounded-2xl border border-dashed border-border bg-secondary/40 p-8 text-center" data-testid="hotel-unmapped-state">
                <Hotel className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="font-semibold">We couldn't find “{data.destination.city}”</p>
                <p className="text-sm text-muted-foreground mt-1">Try searching the city by name in the box above, or pick a popular destination below.</p>
              </div>
            ) : offers.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-secondary/40 p-8 text-center" data-testid="hotel-empty-state">
                <p className="font-semibold">No stays found for these dates</p>
                <p className="text-sm text-muted-foreground mt-1">Try different dates or a nearby destination.</p>
              </div>
            ) : view === 'map' ? (
              <HotelMap offers={offers} currency={currency}
                onBook={(offer) => bookHotel(offer, 'hotel_map')} />
            ) : (
              <>
                <FilteredResults schema={SCHEMAS.hotel} items={offers} testId="hotel-results" countNoun="hotel"
                  gridClassName="space-y-3"
                  renderItem={(o) => <HotelCard key={o.offer_id} offer={o} currency={currency} onOpenDetail={openHotelPage} onViewDeal={(offer) => bookHotel(offer, 'hotel_results')} />} />
                {data.has_more && (
                  <div className="flex justify-center mt-5">
                    <Button variant="outline" onClick={loadMore} disabled={loadingMore || streaming} className="h-11 px-8 rounded-xl font-semibold" data-testid="hotel-load-more">
                      {(loadingMore || streaming) ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…</> : `Load more stays`}
                    </Button>
                  </div>
                )}
              </>
            )}
            <ExperienceCrossSell city={data.destination?.city || ''} code={data.destination?.code || ''}
              currency={currency} source="/hotels" />
          </>
        )}

        {!loading && !data && (
          <div data-testid="hotel-discovery">
            {!discovery && <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}</div>}
            {discovery && discovery.destinations?.length > 0 && (
              <DestinationTiles destinations={discovery.destinations} onSelect={selectDestination} />
            )}
            <FaqSection
              title="Hotel booking FAQs"
              faqs={[
                { q: 'Does FlightDeals.in charge booking fees for hotels?', a: 'No. FlightDeals.in is a hotel comparison service — we show live prices and you complete your booking with our trusted hotel partner, with no added booking fees from us.' },
                { q: 'Are the hotel prices live?', a: 'Yes. Prices and availability are fetched live for your selected dates and shown in INR by default (with a currency switcher). The final price is always confirmed with the booking partner before you pay.' },
                { q: 'Who handles my hotel booking, payment and cancellation?', a: 'Your booking, payment, confirmation and any cancellation or refund are handled directly by the hotel booking partner. FlightDeals.in simply redirects you there to complete the reservation.' },
                { q: 'How many hotels can I compare?', a: 'You can search essentially any city worldwide across a catalogue of thousands of stays, filter by price, star rating, guest rating, breakfast and Wi-Fi, and view results as a list or on a map.' },
                { q: 'Can I search small towns?', a: 'Yes. Type any destination and pick it from the suggestions — coverage spans tens of thousands of cities and towns, including smaller and regional destinations.' },
              ]}
            />
          </div>
        )}
      </section>
    </Layout>
  );
}
